function clamp(n, min, max) {
  const v = Number(n);
  if (!Number.isFinite(v)) return min;
  return Math.min(max, Math.max(min, v));
}

export function normalizeHex(input) {
  const raw = String(input || '').trim();
  if (!raw) return '';
  const s = raw.startsWith('#') ? raw.slice(1) : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(s)) return '';
  return `#${s.toUpperCase()}`;
}

function hexToRgb(hex) {
  const h = normalizeHex(hex);
  if (!h) return null;
  const r = parseInt(h.slice(1, 3), 16);
  const g = parseInt(h.slice(3, 5), 16);
  const b = parseInt(h.slice(5, 7), 16);
  return { r, g, b };
}

function srgbToLinear(c) {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

export function relativeLuminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const r = srgbToLinear(rgb.r);
  const g = srgbToLinear(rgb.g);
  const b = srgbToLinear(rgb.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(fgHex, bgHex) {
  const fg = normalizeHex(fgHex);
  const bg = normalizeHex(bgHex);
  if (!fg || !bg) return 1;
  const L1 = relativeLuminance(fg);
  const L2 = relativeLuminance(bg);
  const lighter = Math.max(L1, L2);
  const darker = Math.min(L1, L2);
  return (lighter + 0.05) / (darker + 0.05);
}

export function pickReadableText(bgHex) {
  const bg = normalizeHex(bgHex);
  if (!bg) return '#FFFFFF';
  const white = '#FFFFFF';
  const black = '#0B0703';
  const cw = contrastRatio(white, bg);
  const cb = contrastRatio(black, bg);
  return cw >= cb ? white : black;
}

function mixHex(a, b, t) {
  const A = hexToRgb(a);
  const B = hexToRgb(b);
  if (!A || !B) return normalizeHex(a) || normalizeHex(b) || '#000000';
  const tt = clamp(t, 0, 1);
  const r = Math.round(A.r + (B.r - A.r) * tt);
  const g = Math.round(A.g + (B.g - A.g) * tt);
  const bb = Math.round(A.b + (B.b - A.b) * tt);
  const toHex = (n) => n.toString(16).padStart(2, '0').toUpperCase();
  return `#${toHex(r)}${toHex(g)}${toHex(bb)}`;
}

export function makeColorSystemFromPalette(palette, mode = 'dark') {
  const pal = Array.isArray(palette) ? palette.map(normalizeHex).filter(Boolean) : [];
  const uniq = Array.from(new Set(pal));
  const primary = uniq[0] || (mode === 'dark' ? '#B77A2A' : '#B77A2A');
  const secondary = uniq[1] || (mode === 'dark' ? '#6E3E14' : '#6E3E14');
  const accent = uniq[2] || (mode === 'dark' ? '#F5C55B' : '#F5C55B');

  const bg = mode === 'dark' ? '#070503' : '#FFF7EC';
  const surface = mode === 'dark' ? mixHex(bg, '#FFFFFF', 0.06) : mixHex(bg, '#000000', 0.04);
  const border = mode === 'dark' ? mixHex(primary, '#FFFFFF', 0.18) : mixHex(primary, '#000000', 0.18);

  const text = pickReadableText(bg);
  const textOnPrimary = pickReadableText(primary);
  const textMuted = mode === 'dark' ? mixHex(text, bg, 0.45) : mixHex(text, bg, 0.50);

  return {
    mode,
    roles: {
      primary,
      secondary,
      accent,
      background: bg,
      surface,
      border,
      text,
      textMuted,
      textOnPrimary,
    },
    locks: {
      primary: false,
      secondary: false,
      accent: false,
      background: false,
      surface: false,
      border: false,
      text: true,
      textMuted: true,
      textOnPrimary: true,
    },
  };
}

export function ensureBrandColorSystem(brand) {
  const b = brand && typeof brand === 'object' ? brand : null;
  if (!b) return null;
  const existing = b.colorSystem;
  if (existing && existing.dark?.roles && existing.light?.roles) return existing;
  const palette = b.palette || [];
  return {
    dark: makeColorSystemFromPalette(palette, 'dark'),
    light: makeColorSystemFromPalette(palette, 'light'),
  };
}

